import React, { useId, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { CellContext } from '@tanstack/react-table';
import { EditorContent } from '@tiptap/react';

import { CoverPlaceholder } from '../../strip-board/components/cover-placeholder';
import { Image, HiddenCoverOverlay } from '../../schedule/components/styled-strips';
import { ShotLocationSelector } from '../../schedule/components/shot-location-selector';
import { ShotCharactersSelector } from '../../schedule/components/shot-characters-selector';
import { InlineTimeSelector } from '../../schedule/components/InlineTimeSelector';
import {
  InlineTimePeriodSelector,
  InlineTimePeriodSelectorRef,
} from '../../schedule/components/InlineTimePeriodSelector';
import { Shot } from '../../../app/entities';
import { Property } from '../../../app/entities/property';
import { TagsContainer, TagsViewer } from '../../tags/components/tags-viewer';
import { useRichTextEditor } from '@producer/ui-kit';
import { MultiSelectDropdown, SingleSelectDropdown } from '../../../components/select-dropdown';
import { TagLabel } from '../../tags/components/styled-tags-components';
import { HistoryInput, HistoryInputRef } from '../../../components/history-input/history-input';
import { compareArrays } from '../../../lib/utils/compare-two-arrays';

import shared from '../stripboard.module.scss';
import styles from './stripboard-cells.module.scss';

interface CellProps extends CellContext<any, any> {
  property: Property;
  handleEdit?: (data: any) => void;
}

export const SmallTextCell: React.FC<CellProps> = observer(({ row, property, handleEdit }) => {
  const inputRef = useRef<HistoryInputRef>(null);

  const strip = row.original;

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);
    const value = shot.getProperty(property._id);

    if (!shot) {
      return <div />;
    }

    const handleSubmit = (value: string) => {
      handleEdit?.({ properties: { [property._id]: value } });
    };

    return (
      <div className={shared.shotCell} onClick={() => inputRef.current?.focus()}>
        <HistoryInput ref={inputRef} value={value} onChange={handleSubmit} />
      </div>
    );
  }

  return <div />;
});

export const LongTextCell: React.FC<CellProps> = observer(({ row, property, handleEdit }) => {
  const strip = row.original;
  const key = useId();

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);
    const value = shot.getProperty(property._id);

    const { editor } = useRichTextEditor({
      key,
      value,
      onBlur: (text) => {
        handleEdit?.({ properties: { [property._id]: text } });
      },
    });

    if (!shot) {
      return <div />;
    }

    return (
      <div className={shared.shotCell} onClick={() => editor.commands.focus()}>
        <EditorContent className={styles['notes-input']} editor={editor} placeholder="Start typing" />
      </div>
    );
  }

  return <div />;
});

export const TagCell: React.FC<CellProps> = observer(({ row, handleEdit }) => {
  const strip = row.original;

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);

    if (!shot) {
      return <div />;
    }

    const currTags = [...shot.tagsIds];

    return (
      <div className={shared.shotCell}>
        <TagsViewer
          tagsIds={shot.tagsIds}
          targetType="shot"
          onSelect={(tagsIds) => {
            const { added, deleted } = compareArrays(currTags, tagsIds);
            const tagIdToRemove = deleted.length > 0 ? deleted[0] : undefined;
            const tagIdToAdd = added.length > 0 ? added[0] : undefined;

            handleEdit?.({ tagIdToAdd, tagIdToRemove, tagsIds });
          }}
        />
      </div>
    );
  }

  return <div />;
});

export const SingleSelectCell: React.FC<CellProps> = observer(({ row, property, handleEdit }) => {
  const strip = row.original;

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);

    if (!shot) {
      return <div />;
    }

    const options = property.options
      ? property.options.map((option) => ({
          label: option,
          value: option,
        }))
      : [];

    const value = shot.getProperty(property._id);

    return (
      <SingleSelectDropdown
        value={value}
        title={property.name}
        onValueChange={(value) => {
          handleEdit?.({ properties: { [property._id]: value } });
        }}
        options={options}
        trigger={(value) => (
          <div className={shared.shotCell}>
            <span>{value}</span>
          </div>
        )}
      />
    );
  }

  return <div />;
});

export const MultiSelectCell: React.FC<CellProps> = observer(({ row, property, handleEdit }) => {
  const strip = row.original;

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);

    if (!shot) {
      return <div />;
    }

    const options = property.options
      ? property.options.map((option) => ({
          label: option,
          value: option,
        }))
      : [];

    let value = shot.getProperty(property._id);
    value = Array.isArray(value) ? value : [value];
    value = value.filter((el) => property.options.includes(el));

    const handleChange = (newValue: string, isChecked: boolean) => {
      const values = value && Array.isArray(value) ? value : [];

      isChecked ? values.push(newValue) : values.splice(values.indexOf(newValue), 1);

      handleEdit?.({ properties: { [property._id]: values } });
    };

    return (
      <MultiSelectDropdown
        title={property.name}
        selectedOptions={value}
        onValueChange={handleChange}
        options={options}
        trigger={(values) => (
          <div className={shared.shotCell}>
            <TagsContainer data-wrap={true}>
              {values &&
                values.split(',').map((value) => (
                  <TagLabel>
                    <span>{value}</span>
                  </TagLabel>
                ))}
            </TagsContainer>
          </div>
        )}
      />
    );
  }

  return <div />;
});

export const ShotCell: React.FC<CellProps> = observer(({ row }) => {
  const strip = row.original;

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);

    if (!shot) {
      return <div />;
    }

    return (
      <div className={shared.shotCell} data-no-padding={true}>
        <div className={shared.cover}>
          {shot.cover?.src ? (
            <Image src={shot.cover?.src || ''} />
          ) : (
            <CoverPlaceholder onUpload={(file) => shot.update({ file })} />
          )}

          <HiddenCoverOverlay data-hide={!strip.data.isHidden} />
        </div>
      </div>
    );
  }

  return <div />;
});

export const LocationCell: React.FC<CellProps> = observer(({ row, property, handleEdit }) => {
  const strip = row.original;

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);

    if (!shot?.project) {
      return <div />;
    }

    return (
      <div className={`${shared.locationCell}`}>
        <ShotLocationSelector
          project={shot.project}
          onChange={(locationId) => handleEdit({ locationId })}
          selectedLocationId={shot.locationId}
        />
      </div>
    );
  }

  return <div />;
});

export const CharactersCell: React.FC<CellProps> = observer(({ row, handleEdit }) => {
  const strip = row.original;

  if (strip.type === 'banner') {
    return <div />;
  }

  if (strip.type === 'shot') {
    const shot = Shot.getOne(strip.data.shotId);

    if (!shot) {
      return <div />;
    }

    const handleRemoveCharacter = (characterIdToRemove: string) => {
      handleEdit?.({ characterIdToRemove });
    };

    const handleAddCharacter = (characterIdToAdd: string) => {
      handleEdit?.({ characterIdToAdd });
    };

    return (
      <ShotCharactersSelector
        shot={shot}
        onAdd={handleAddCharacter}
        onRemove={handleRemoveCharacter}
        trigger={(values) => (
          <div className={`${shared.shotCell}`}>
            <TagsContainer data-wrap={true}>
              {values &&
                values.split(',').map((value) => (
                  <TagLabel>
                    <span>{value}</span>
                  </TagLabel>
                ))}
            </TagsContainer>
          </div>
        )}
      />
    );
  }

  return <div />;
});

export const EstimatedTimeCell: React.FC<CellProps> = observer(({ row, table, handleEdit }) => {
  const ref = useRef<InlineTimePeriodSelectorRef>(null);
  const strip = row.original;

  if (['shot', 'banner'].includes(strip.type)) {
    const shot = Shot.getOne(strip.data.shotId);

    if (strip.data.isHidden) {
      return <div />;
    }

    const estimatedTime = shot?.estimatedTime || strip.data.estimatedTime;

    const handleOnChange = (estimatedTime: number) => {
      if (shot) {
        handleEdit?.({ estimatedTime });
      } else {
        const onUpdateStrip = table.options.meta?.onUpdateStrip;

        onUpdateStrip?.(strip._id, {
          data: {
            estimatedTime,
          },
        });
      }
    };

    return (
      <div className={shared.shotCell} onClick={() => ref.current?.open()}>
        <InlineTimePeriodSelector ref={ref} onChange={handleOnChange} time={estimatedTime} />
      </div>
    );
  }

  return <div />;
});

export const StartTimeCell: React.FC<CellProps> = observer(({ row }) => {
  const strip = row.original;

  if (['shot', 'banner'].includes(strip.type)) {
    if (!strip.startTime && !strip.data.estimatedTime) {
      return <div />;
    }

    return (
      <div className={shared.shotCell} data-disabled={true}>
        <InlineTimeSelector time={strip.startTime || { hour: 0, minute: 0 }} disabled />
      </div>
    );
  }

  return <div />;
});
